import { useTranslation } from "react-i18next";
import { DashboardTile } from "@idwal/idwal-react-components";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { openUrl } from "../../services/WindowOpenService";
import { sendGAEvent } from "../../AnalyticsService";
import { DASHBOARD_CATEGORY, DASHBOARD_LATEST_INSPECTION_CLICK, DASHBOARD_SCHEDULED_INSPECTIONS_CLICK } from "../../constants/analytics";

interface CampaignTilesProps {
    latestInspection?: {
        shipImo: string;
        shipName: string;
        issuedAt: string;
        idwalGrade: number;
        guid: string;
    };
    campaignMetrics?: {
        scheduledInspections: number;
        inspectionYtd: number;
        commitmentCompletionPercentage: number;
        commitmentVolume?: number;
        hasActiveContract: boolean;
    };
    loading?: boolean;
}

export const CampaignTiles = ({ latestInspection, campaignMetrics, loading }: CampaignTilesProps) => {
    const { t } = useTranslation("locale");
    const navigate = useNavigate();

    const onLatestInspectionClick = useCallback(() => {
        sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_LATEST_INSPECTION_CLICK);
        if (latestInspection?.guid) {
            const reportUrl = `${process.env.REACT_APP_ID_PLATFORM_CUSTOMER_REPORT_URL_ROOT}/Inspection/${latestInspection.guid}`;
            openUrl(reportUrl);
        }
    }, [latestInspection]);

    const onScheduledInspectionsClick = useCallback(() => {
        const statusParams = [
            t("inspectionStatuses.ordered"),
            t("inspectionStatuses.inProgress"),
            t("inspectionStatuses.inspectionComplete")
        ].join(',');

		sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_SCHEDULED_INSPECTIONS_CLICK);
        navigate(`/?inspectionStatus=${encodeURIComponent(statusParams)}`);
    }, [navigate, t]);

    const tiles = useMemo(() => {
        const tileArray = [];

        // Latest inspection tile - always show
        if (latestInspection?.idwalGrade !== undefined && latestInspection?.idwalGrade !== null) {
            tileArray.push({
                metric: t("dashboard.latestInspection.title"),
                value: latestInspection.idwalGrade,
                grade: latestInspection.idwalGrade,
                label: t("dashboard.latestInspection.label", { 
                    date: latestInspection.issuedAt 
                        ? new Date(latestInspection.issuedAt).toLocaleDateString('en-GB')
                        : 'N/A'
                }),
                unit: t("dashboard.latestInspection.unit", {
                    shipName: latestInspection.shipName || 'Unknown',
                    shipImo: latestInspection.shipImo || 'N/A'
                }),
                onClick: onLatestInspectionClick,
                elements: {
                    colourBar: true,
                    label: true,
                    link: true
                }
            });
        } else {
            tileArray.push({
                metric: t("dashboard.latestInspection.title"),
                value: null,
                label: t("dashboard.noDataAvailable"),
                unit: "",
                elements: {
                    label: true
                }
            });
        }
        
        tileArray.push({
            metric: t("dashboard.scheduledInspections.title"),
            value: campaignMetrics?.scheduledInspections ?? 0,
            label: t("dashboard.scheduledInspections.label"),
            unit: t("dashboard.scheduledInspections.unit"),
            onClick: onScheduledInspectionsClick,
            elements: {
                label: true,
                link: true
            }
        });

        if (campaignMetrics?.hasActiveContract) {
            // Contracted commitment tile - only show if active contract
            tileArray.push({
                metric: t("dashboard.inspectionYtd.title"),
                value: campaignMetrics?.inspectionYtd ?? 0,
                label: t("dashboard.inspectionYtd.label", {
                    percentage: campaignMetrics?.commitmentCompletionPercentage ?? 0,
                    commitment: campaignMetrics?.commitmentVolume || 0
                }),
                unit: t("dashboard.inspectionYtd.unit"),
                elements: {
                    label: true
                }
            });
        }

        return tileArray;
    }, [latestInspection, campaignMetrics, t, onLatestInspectionClick, onScheduledInspectionsClick, loading]);

    // Determine responsive column classes based on number of tiles
    const getColumnClasses = () => {
        const tileCount = tiles.length;
        if (tileCount === 1) {
            return "col-12"; // Single tile: full width on all screen sizes
        } else if (tileCount === 2) {
            return "col-12 md:col-6"; // Two tiles: full width mobile, half on larger screens
        }
        return "col-12 md:col-6 lg:col-4"; // Three tiles: full width mobile, half on tablet, 1/3 on desktop
    };

    return (
        <>
                <div className="col-12">
                    <h2>{t("dashboard.campaignSummary.title")}</h2>
                </div>
            {tiles.map((tile, index) => (
                <div key={index} className={getColumnClasses()}>
                    <DashboardTile
                        data={tile}
                        elements={tile.elements}
                        loading={loading}
                    />
                </div>
            ))}
        </>
    );
};
